'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';

/**
 * How long each face (coordinates, then meta) holds at ≤720px — half of the
 * 12s `--dur-strip-crossfade` cycle (WEB-621 / DES-61 / PL-10).
 */
export const COORD_STRIP_FACE_MS = 6000;

/**
 * One cutoff for city, apex, and app (WEB-1136). Crossfade at ≤720; both
 * faces from 721. Same strings as `styles/overrides/chrome.css` and
 * `styles/overrides/reduced-motion.css` — the DS 721 cut, not Tailwind `md`.
 */
export const COORD_STRIP_CROSSFADE_QUERY = '(max-width: 720px)';
export const COORD_STRIP_STATIC_QUERY = '(min-width: 721px)';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export type CoordStripFace = 'pos' | 'meta';

export interface CoordStripCrossfadeHostProps {
  children: React.ReactNode;
  className?: string;
  /** Optional test id for the crossfade host (not the layers). */
  testId?: string;
  /** Hides both layers from AT when the strip carries one accessible string. */
  ariaHidden?: true;
}

function matches(query: string): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/**
 * Mobile host for the coordinates ⇄ meta crossfade (WEB-621 / DES-61).
 *
 * WEB-1131 (P0-1): ONE attribute — `data-face` — drives both layers. The
 * previous version ran two independent CSS animations (`loci-strip-a` /
 * `loci-strip-b`), one per layer, with no resting opacity on the meta layer.
 * Two timelines can drift apart, and an animation that is not running leaves
 * both layers at opacity 1; either way iOS Safari painted
 * `35.2271° N ·2002 EVENTS` on top of each other for seconds. With a single
 * source of truth the layers cannot disagree, and before hydration (or with
 * JS off) the CSS resting state shows coordinates only.
 *
 * The timer only runs where the crossfade is visible: ≤720px, without
 * `prefers-reduced-motion: reduce`, and while the tab is visible. Reduced
 * motion holds the coordinates (the continuity device, F-11). The fade itself
 * is CSS (`[data-coord-crossfade]` in `styles/overrides/chrome.css`).
 */
export function CoordStripCrossfadeHost({
  children,
  className,
  testId,
  ariaHidden,
}: CoordStripCrossfadeHostProps): React.ReactElement {
  const [face, setFace] = React.useState<CoordStripFace>('pos');

  React.useEffect(() => {
    let timer: number | undefined;
    const stop = (): void => {
      if (timer === undefined) return;
      window.clearInterval(timer);
      timer = undefined;
    };
    const sync = (): void => {
      const animates = matches(COORD_STRIP_CROSSFADE_QUERY) && !matches(REDUCED_MOTION_QUERY);
      if (!animates) {
        stop();
        setFace('pos');
        return;
      }
      // A hidden tab freezes on the current face rather than resetting it.
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      if (timer === undefined) {
        timer = window.setInterval(() => {
          setFace((current) => (current === 'pos' ? 'meta' : 'pos'));
        }, COORD_STRIP_FACE_MS);
      }
    };

    sync();
    const queries =
      typeof window.matchMedia === 'function'
        ? [window.matchMedia(COORD_STRIP_CROSSFADE_QUERY), window.matchMedia(REDUCED_MOTION_QUERY)]
        : [];
    for (const query of queries) query.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      stop();
      for (const query of queries) query.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return (
    <span
      data-coord-crossfade=""
      data-face={face}
      data-testid={testId}
      aria-hidden={ariaHidden}
      className={cn(className)}
    >
      {children}
    </span>
  );
}
